import { type FC, type ReactNode, useCallback, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import classNames from 'classnames'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'

import Typography from '~/components/elements/Typography/Typography'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { AgentLink } from './AgentLink'

import styles from './MarkdownComponents.module.scss'

type MdComponentProps = {
  [key: string]: any
  children?: ReactNode
  className?: string
  node?: any // React-markdown passes this
}

const excludeNonDomProps = (props: Record<string, any>) => {
  const { node, ...domProps } = props
  return domProps
}

const createMdComponents = (
  onLinkClick?: (href: string, linkType: 'internal' | 'external') => void,
) => ({
  h1: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.H1 className={className} {...excludeNonDomProps(props)}>
      {children}
    </Typography.H1>
  ),
  h2: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.H2 className={className} {...excludeNonDomProps(props)}>
      {children}
    </Typography.H2>
  ),
  h3: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.H3 className={className} {...excludeNonDomProps(props)}>
      {children}
    </Typography.H3>
  ),
  p: ({ className, children, ...props }: MdComponentProps) => (
    <Typography.Paragraph className={className} {...excludeNonDomProps(props)}>
      {children}
    </Typography.Paragraph>
  ),
  a: ({ className, children, href, ...props }: MdComponentProps) => {
    const domProps = excludeNonDomProps(props)

    if (!href) {
      return (
        <a className={className} {...domProps}>
          {children}
        </a>
      )
    }

    // Commenting out the SOAP and LabTest buttons for now as a part of CVC-35590. Will be re-introduced in the future.
    // Check if this is a SOAP link - convert any /soap/ link to SoapLinkButton
    // if (href.startsWith('/soap/')) {
    //   const urlParts = href.split('?')
    //   const pathname = urlParts[0]
    //   const queryParams = urlParts[1] || undefined
    //   const soapIdMatch = pathname.match(/^\/soap\/([A-Za-z0-9_-]+)$/)

    //   if (soapIdMatch) {
    //     const soapId = soapIdMatch[1]
    //     return (
    //       <SoapLinkButton
    //         className={className}
    //         queryParams={queryParams}
    //         soapId={soapId}
    //         onLinkClick={() => onLinkClick?.(href, 'internal')}
    //       >
    //         {children}
    //       </SoapLinkButton>
    //     )
    //   }
    // }

    // Check if this is a lab tests dashboard link - convert to LabTestLinkButton
    // const labDashboardParsed = parseLabTestDashboardUrl(href)
    // if (labDashboardParsed) {
    //   return (
    //     <LabTestLinkButton
    //       className={className}
    //       href={href}
    //       onLinkClick={() => onLinkClick?.(href, 'internal')}
    //     >
    //       {children}
    //     </LabTestLinkButton>
    //   )
    // }

    if (href.startsWith('/') || href.startsWith('#')) {
      return (
        <AgentLink
          className={className}
          href={href}
          onClick={() => onLinkClick?.(href, 'internal')}
          {...domProps}
        >
          {children}
        </AgentLink>
      )
    }

    try {
      const linkUrl = new URL(href)
      // Whitelist of allowed internal hostnames to prevent security issues
      // Matches: pims*rhapsody.vet, portal*rhapsody.vet (where * can be . or env like -dev, -staging)
      // Also matches: rhapsody.com (exact match only)
      // Note: rhapsody.com is included because the AI agent sometimes generates links with this domain
      // instead of rhapsody.vet. This is expected behavior we need to handle.
      const { hostname } = linkUrl
      const isInternal =
        /^pims.*\.rhapsody\.vet$/.test(hostname) ||
        /^portal.*\.rhapsody\.vet$/.test(hostname) ||
        hostname === 'rhapsody.com'
      const hasNullBusinessId =
        linkUrl.search.includes('businessId') &&
        linkUrl.searchParams.get('businessId') === 'None'
      if (hasNullBusinessId) {
        linkUrl.searchParams.delete('businessId')
      }
      if (isInternal) {
        const internalPath = `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`
        return (
          <AgentLink
            className={className}
            href={internalPath}
            onClick={() => onLinkClick?.(href, 'internal')}
            {...domProps}
          >
            {children}
          </AgentLink>
        )
      }
    } catch (error) {
      // If URL parsing fails, treat as external link
    }

    return (
      <a
        className={className}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
        onClick={() => onLinkClick?.(href, 'external')}
        {...domProps}
      >
        {children}
      </a>
    )
  },
  ul: ({ className, children, ...props }: MdComponentProps) => (
    <ul className={className} {...excludeNonDomProps(props)}>
      {children}
    </ul>
  ),
  ol: ({ className, children, ...props }: MdComponentProps) => (
    <ol className={className} {...excludeNonDomProps(props)}>
      {children}
    </ol>
  ),
  li: ({ className, children, ...props }: MdComponentProps) => (
    <li className={className} {...excludeNonDomProps(props)}>
      {children}
    </li>
  ),
  blockquote: ({ className, children, ...props }: MdComponentProps) => (
    <blockquote className={className} {...excludeNonDomProps(props)}>
      {children}
    </blockquote>
  ),
  code: ({ className, children, ...props }: MdComponentProps) => (
    <code className={className} {...excludeNonDomProps(props)}>
      {children}
    </code>
  ),
  pre: ({ className, children, ...props }: MdComponentProps) => (
    <pre className={className} {...excludeNonDomProps(props)}>
      {children}
    </pre>
  ),
  hr: ({ className, ...props }: MdComponentProps) => (
    <hr className={className} {...excludeNonDomProps(props)} />
  ),
  table: ({ className, children, ...props }: MdComponentProps) => (
    <div className={styles.tableWrapper}>
      <table
        className={classNames(styles.table, className)}
        {...excludeNonDomProps(props)}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ className, children, ...props }: MdComponentProps) => (
    <th
      className={classNames(styles.tableHeader, className)}
      {...excludeNonDomProps(props)}
    >
      {children}
    </th>
  ),
  td: ({ className, children, ...props }: MdComponentProps) => (
    <td
      className={classNames(styles.tableCell, className)}
      {...excludeNonDomProps(props)}
    >
      {children}
    </td>
  ),
  strong: ({ className, children, ...props }: MdComponentProps) => (
    <strong className={className} {...excludeNonDomProps(props)}>
      {children}
    </strong>
  ),
  em: ({ className, children, ...props }: MdComponentProps) => (
    <em className={className} {...excludeNonDomProps(props)}>
      {children}
    </em>
  ),
})

/** Component overrides for MarkdownRenderer. These are merged over the default markdown components. */
export type MarkdownComponentOverrides = Partial<
  ReturnType<typeof createMdComponents>
>

export const MarkdownRenderer: FC<{
  /** Optional overrides for markdown components */
  componentOverrides?: MarkdownComponentOverrides
  content: string
  sessionId?: string | null
}> = ({ content, sessionId, componentOverrides }) => {
  const handleLinkClick = useCallback(
    (href: string, linkType: 'internal' | 'external') => {
      trackEvent(TrackingEvent.AI_ASSISTANT_RESPONSE_LINK_CLICKED, {
        link_type: linkType,
        link_href: href,
        agent_session_id: sessionId,
      })
    },
    [sessionId],
  )

  const mdComponents = useMemo(
    () => ({
      ...createMdComponents(handleLinkClick),
      ...componentOverrides,
    }),
    [handleLinkClick, componentOverrides],
  )

  return (
    <div className="markdown-content">
      <ReactMarkdown
        components={mdComponents}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        remarkPlugins={[remarkGfm]}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownRenderer
